import { FC, useState, useCallback, useRef, Children, cloneElement, isValidElement } from 'react';
import cn from 'bem-cn';

import { Icon } from '../Icon';
import { SliderProps, SliderState, TouchState } from './Slider.types';

import './Slider.scss';

const b = cn('slider');

export const Slider: FC<SliderProps> = ({
    children,
    className,
    showArrows = true,
    showDots = true,
    infinite = false,
    onSlideChange,
    initialSlide = 0,
    speed = 300,
    swipe = true,
    swipeThreshold = 50,
}) => {
    const sliderRef = useRef<HTMLDivElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const touchStateRef = useRef<TouchState>({
        startX: 0,
        currentX: 0,
        isTouching: false,
        offset: 0,
    });

    const childrenArray = Children.toArray(children);
    const totalSlides = childrenArray.length;

    const [state, setState] = useState<SliderState>(() => ({
        currentSlide: initialSlide,
        totalSlides,
        isAnimating: false,
    }));

    // Функция для перехода к слайду
    const goToSlide = useCallback(
        (slideIndex: number) => {
            if (state.isAnimating || totalSlides === 0) {
                return;
            }

            let targetSlide = slideIndex;

            if (infinite) {
                if (targetSlide < 0) {
                    targetSlide = totalSlides - 1;
                } else if (targetSlide >= totalSlides) {
                    targetSlide = 0;
                }
            } else {
                targetSlide = Math.max(0, Math.min(targetSlide, totalSlides - 1));
            }

            setState(prev => ({
                ...prev,
                currentSlide: targetSlide,
                isAnimating: true,
            }));

            onSlideChange?.(targetSlide);

            setTimeout(() => {
                setState(prev => ({
                    ...prev,
                    isAnimating: false,
                }));
            }, speed);
        },
        [state.isAnimating, totalSlides, infinite, onSlideChange, speed],
    );

    // Переход к следующему слайду
    const nextSlide = useCallback(() => {
        const nextIndex = state.currentSlide + 1;
        goToSlide(nextIndex);
    }, [state.currentSlide, goToSlide]);

    // Переход к предыдущему слайду
    const prevSlide = useCallback(() => {
        const prevIndex = state.currentSlide - 1;
        goToSlide(prevIndex);
    }, [state.currentSlide, goToSlide]);

    // Touch события для свайпа
    const handleTouchStart = useCallback(
        (e: React.TouchEvent) => {
            if (!swipe) {
                return;
            }

            touchStateRef.current = {
                startX: e.touches[0].clientX,
                currentX: e.touches[0].clientX,
                isTouching: true,
                offset: 0,
            };
        },
        [swipe],
    );

    const handleTouchMove = useCallback(
        (e: React.TouchEvent) => {
            if (!swipe || !touchStateRef.current.isTouching) {
                return;
            }

            touchStateRef.current.currentX = e.touches[0].clientX;
            touchStateRef.current.offset = touchStateRef.current.currentX - touchStateRef.current.startX;

            // Применяем смещение к треку
            if (trackRef.current) {
                const offsetPercent = (touchStateRef.current.offset / trackRef.current.offsetWidth) * 100;
                const translateX = -state.currentSlide * 100 + offsetPercent;
                trackRef.current.style.transform = `translateX(${translateX}%)`;
            }
        },
        [swipe, state.currentSlide],
    );

    const handleTouchEnd = useCallback(() => {
        if (!swipe || !touchStateRef.current.isTouching) {
            return;
        }

        const { offset } = touchStateRef.current;

        // Проверяем возможность навигации
        const canGoPrev = infinite || state.currentSlide > 0;
        const canGoNext = infinite || state.currentSlide < totalSlides - 1;

        if (Math.abs(offset) > swipeThreshold) {
            if (offset > 0 && canGoPrev) {
                prevSlide();
            } else if (offset < 0 && canGoNext) {
                nextSlide();
            } else if (trackRef.current) {
                // Возвращаем к исходной позиции, если свайп невозможен
                const translateX = -state.currentSlide * 100;
                trackRef.current.style.transform = `translateX(${translateX}%)`;
            }
        } else if (trackRef.current) {
            // Возвращаем к исходной позиции
            const translateX = -state.currentSlide * 100;
            trackRef.current.style.transform = `translateX(${translateX}%)`;
        }

        touchStateRef.current.isTouching = false;
    }, [swipe, swipeThreshold, prevSlide, nextSlide, state.currentSlide, infinite, totalSlides]);

    // Рендер слайдов
    const renderSlides = () => {
        return childrenArray.map((child, index) => (
            <div key={index} className={b('slide')} style={{ width: '100%' }}>
                {isValidElement(child) ? cloneElement(child) : child}
            </div>
        ));
    };

    // Рендер индикаторов
    const renderDots = () => {
        if (!showDots || totalSlides <= 1) {
            return null;
        }

        return (
            <div className={b('dots')}>
                {Array.from({ length: totalSlides }, (_, index) => {
                    const isActive = index === state.currentSlide;

                    return (
                        <button
                            key={index}
                            type="button"
                            className={b('dot', { active: isActive })}
                            onClick={() => goToSlide(index)}
                            aria-label={`Перейти к слайду ${index + 1}`}
                        />
                    );
                })}
            </div>
        );
    };

    // Проверка возможности навигации
    const canGoPrev = infinite || state.currentSlide > 0;
    const canGoNext = infinite || state.currentSlide < totalSlides - 1;

    const sliderClasses = b({
        'with-arrows': showArrows,
        'with-dots': showDots,
        animating: state.isAnimating,
    }).mix(className);

    const trackTransform = touchStateRef.current.isTouching ? undefined : `translateX(-${state.currentSlide * 100}%)`;

    return (
        <div
            ref={sliderRef}
            className={sliderClasses}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <div className={b('container')}>
                {showArrows && (
                    <button
                        type="button"
                        className={b('arrow', { direction: 'prev', disabled: !canGoPrev })}
                        onClick={prevSlide}
                        disabled={!canGoPrev}
                        aria-label="Предыдущий слайд"
                    >
                        <Icon name="backArrow" />
                    </button>
                )}

                <div className={b('viewport')}>
                    <div
                        ref={trackRef}
                        className={b('track')}
                        style={{
                            transform: trackTransform,
                            transitionDuration: state.isAnimating ? `${speed}ms` : '0ms',
                        }}
                    >
                        {renderSlides()}
                    </div>
                </div>

                {showArrows && (
                    <button
                        type="button"
                        className={b('arrow', { direction: 'next', disabled: !canGoNext })}
                        onClick={nextSlide}
                        disabled={!canGoNext}
                        aria-label="Следующий слайд"
                    >
                        <Icon name="chevronRight" />
                    </button>
                )}
            </div>

            {renderDots()}
        </div>
    );
};
